<template>
  <v-dialog v-model="open" max-width="560" :persistent="uploading">
    <v-card>
      <v-card-title class="d-block">
        <div>{{ hasPhoto ? "Change photo" : "Add photo" }}</div>
        <div class="caption grey--text mt-1">Please read and agree before uploading.</div>
      </v-card-title>

      <v-card-text class="pt-2">
        <v-alert v-if="error" type="error" dense text class="mb-4">
          {{ error }}
        </v-alert>

        <v-checkbox
          v-model="accepted"
          :disabled="uploading"
          hide-details
          class="mt-0 pt-0 terms-check"
        >
          <template v-slot:label>
            <span class="terms-text">{{ terms }}</span>
          </template>
        </v-checkbox>
      </v-card-text>

      <v-card-actions class="px-6 pb-4">
        <v-spacer />
        <v-btn text :disabled="uploading" @click="open = false">Cancel</v-btn>
        <v-btn
          color="primary"
          depressed
          :disabled="!accepted"
          :loading="uploading"
          @click="$emit('choose')"
        >
          Choose photo
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import Vue from "vue";

// Wording supplied by ASCA's General Counsel. Change it only on their instruction,
// and bump DogPhotoTerms.CurrentVersion on the server at the same time, so the
// version recorded against each upload still describes what the member agreed to.
// Submitted with the upload and checked by the server, so the recorded consent
// names the wording the member actually saw. Matches DogPhotoTerms.CurrentVersion.
export const PHOTO_TERMS_VERSION = "2026-08-26";

const TERMS =
  "By uploading, you confirm that you are at least 18 years old, that this photo " +
  "is yours to share (or you have permission from the rights holder), that it does " +
  "not depict any person, and that it contains no private contact information. You " +
  "grant ASCA a non-exclusive, royalty-free license to host, edit, display, and " +
  "reproduce the photo in ASCA’s digital and print materials, and you agree to " +
  "hold ASCA harmless from any claims arising from disputes over the photo’s " +
  "ownership. You retain any copyright. You may remove your photo at any time. ASCA " +
  "also reserves the right to remove any photo. Materials already produced using the " +
  "photo cannot be recalled.";

export default Vue.extend({
  name: "PhotoTermsDialog",
  props: {
    value: { type: Boolean, default: false },
    hasPhoto: { type: Boolean, default: false },
    uploading: { type: Boolean, default: false },
    // Shown in here rather than on the page, which sits under this dialog's overlay.
    error: { type: String, default: "" },
  },
  data: function () {
    return {
      terms: TERMS,
      accepted: false,
    };
  },
  computed: {
    open: {
      get(): boolean {
        return this.$props.value;
      },
      set(showing: boolean) {
        this.$emit("input", showing);
      },
    },
  },
  watch: {
    value: function (showing: boolean) {
      // Each upload needs its own agreement, so the box starts clear every time.
      if (showing) this.$data.accepted = false;

      // Clear on the way out too, so cancelling after a failure does not leave the
      // message sitting on the page behind.
      this.$emit("clear-error");
    },
  },
});
</script>

<style scoped>
.terms-text {
  font-size: 0.8125rem;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.75);
}
.terms-check >>> .v-input--selection-controls__input {
  align-self: flex-start;
  margin-top: 2px;
}
</style>
